import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  LinearProgress,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { authGet, authPut, authPost } from '../../services/apiService';
import { format } from 'date-fns';

const darkGreenColor = "#1D4434";

/**
 * SnykAdmin component for managing SNYK configuration and syncing
 */
const SnykAdmin = () => {
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [configSuccess, setConfigSuccess] = useState(null);
  const [configError, setConfigError] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  
  const [lastSync, setLastSync] = useState(null);
  const [autoSyncIntervalHours, setAutoSyncIntervalHours] = useState(4);
  const [closedDateMode, setClosedDateMode] = useState('firstIntroduced');
  const [syncCronSchedule, setSyncCronSchedule] = useState('30 0,2,4,6,8,10,12,14 * * *');
  const [cronError, setCronError] = useState('');
  const [recalculating, setRecalculating] = useState(false);
  const [recalcResult, setRecalcResult] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoadingConfig(true);
      const response = await authGet('/api/snyk/config');
      
      if (response.success) {
        setLastSync(response.config.lastSync || null);
        setAutoSyncIntervalHours(response.config.autoSyncIntervalHours || 4);
        setClosedDateMode(response.config.closedDateMode || 'firstIntroduced');
        setSyncCronSchedule(response.config.syncCronSchedule || '30 0,2,4,6,8,10,12,14 * * *');
      } else {
        setConfigError('Failed to load configuration');
      }
    } catch (error) {
      console.error('Error loading SNYK config:', error);
      setConfigError(error.response?.data?.message || 'Failed to load configuration');
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleSaveConfig = async () => {
    setCronError('');
    try {
      setSavingConfig(true);
      setConfigError(null);
      setConfigSuccess(null);

      const response = await authPut('/api/snyk/config', {
        autoSyncIntervalHours: parseInt(autoSyncIntervalHours) || 4,
        closedDateMode,
        syncCronSchedule: syncCronSchedule.trim()
      });
      
      if (response.success) {
        setConfigSuccess('Configuration saved successfully');
        setLastSync(response.config.lastSync || null);
        if (response.config.syncCronSchedule) setSyncCronSchedule(response.config.syncCronSchedule);
        setTimeout(() => setConfigSuccess(null), 5000);
        await loadConfig();
      } else {
        setConfigError(response.message || 'Failed to save configuration');
      }
    } catch (err) {
      console.error('Error saving SNYK config:', err);
      const msg = err.message || 'Failed to save configuration';
      if (msg.toLowerCase().includes('cron')) setCronError(msg);
      else setConfigError(msg);
    } finally {
      setSavingConfig(false);
    }
  };

  // Force Sync SNYK issues using Export API
  const handleForceSync = async () => {
    try {
      setSyncing(true);
      setConfigError(null);
      setConfigSuccess(null);
      setSyncResult(null);
      
      const response = await authPost('/api/snyk/sync-from-url');
      
      if (response.success) {
        setSyncResult(response.summary);
        setConfigSuccess('SNYK issues synced successfully');
        setLastSync(new Date().toISOString());
        setTimeout(() => setConfigSuccess(null), 10000);
        await loadConfig(); // Refresh to get updated timestamps
      } else {
        setConfigError(response.error || response.message || 'Failed to sync SNYK issues');
      }
    } catch (err) {
      console.error('Error syncing SNYK issues:', err);
      setConfigError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to sync SNYK issues');
    } finally {
      setSyncing(false);
    }
  };

  const handleRecalculate = async () => {
    try {
      setRecalculating(true);
      setConfigError(null);
      setRecalcResult(null);

      const response = await authPost('/api/snyk/recalculate-closed-dates');

      if (response.success) {
        setRecalcResult(response);
        setConfigSuccess(`Recalculation complete: ${response.updatedCount?.toLocaleString()} issues updated`);
        setTimeout(() => setConfigSuccess(null), 8000);
      } else {
        setConfigError(response.message || 'Failed to recalculate closed dates');
      }
    } catch (err) {
      console.error('Error recalculating closed dates:', err);
      setConfigError(err.response?.data?.message || err.message || 'Failed to recalculate closed dates');
    } finally {
      setRecalculating(false);
    }
  };

  // Download CSV using SNYK Export API
  const handleDownloadCsv = async () => {
    try {
      setDownloading(true);
      setConfigError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch('/api/snyk/download-csv', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      // Check if response is JSON (error) or CSV (success)
      const contentType = response.headers.get('content-type') || '';
      
      if (!response.ok || contentType.includes('application/json')) {
        // It's an error response
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to download CSV');
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'snyk-issues.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Create blob with explicit CSV type
      const blob = await response.blob();
      
      // Verify it's actually CSV by checking the first few bytes
      const text = await blob.slice(0, 100).text();
      if (text.trim().startsWith('PROJECT_NAME') || text.includes(',')) {
        // It's CSV, proceed with download
        const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv;charset=utf-8;' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setConfigSuccess('CSV file downloaded successfully');
        setTimeout(() => setConfigSuccess(null), 5000);
      } else {
        // It's not CSV, show error
        const fullText = await blob.text();
        throw new Error(`Downloaded file does not appear to be CSV. Content starts with: ${fullText.substring(0, 200)}`);
      }
    } catch (err) {
      console.error('Error downloading CSV:', err);
      setConfigError(err.message || 'Failed to download CSV file');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" mb={3}>
        <SettingsIcon sx={{ mr: 1, color: darkGreenColor }} />
        <Typography variant="h5" component="h1">
          SNYK Configuration
        </Typography>
      </Box>

      {loadingConfig ? (
        <LinearProgress sx={{ mb: 2 }} />
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Auto Sync Configuration
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Auto Sync Interval"
                    value={autoSyncIntervalHours}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 4;
                      if (value >= 1 && value <= 168) {
                        setAutoSyncIntervalHours(value);
                      }
                    }}
                    inputProps={{ min: 1, max: 168, step: 1 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">hours</InputAdornment>
                    }}
                    helperText="Hours between automatic syncs (1-168, default: 4)"
                    disabled={loadingConfig || savingConfig}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box display="flex" alignItems="center" gap={1} sx={{ height: '100%' }}>
                    {lastSync ? (
                      <>
                        <CheckCircleIcon color="success" />
                        <Typography variant="body2" color="text.secondary">
                          Last synced: {format(new Date(lastSync), 'PPpp')}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <ErrorIcon color="warning" />
                        <Typography variant="body2" color="text.secondary">
                          Never synced
                        </Typography>
                      </>
                    )}
                  </Box>
                </Grid>

                {/* Cron schedule */}
                <Grid item xs={12}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    Scheduled Sync (Cron)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Configure when the server automatically runs a SNYK sync. Uses standard 5-part cron syntax in <strong>UTC</strong>.
                    Changes take effect immediately after saving — no restart required.
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
                    {[
                      { label: 'Every 2h · 6AM–8PM IST (default)', value: '30 0,2,4,6,8,10,12,14 * * *' },
                      { label: 'Every 4h · 6AM–8PM IST', value: '30 0,4,8,12 * * *' },
                      { label: 'Every hour · 9AM–5PM IST', value: '0 3,4,5,6,7,8,9,10,11 * * 1-5' },
                      { label: 'Once daily · 6AM IST', value: '30 0 * * *' },
                    ].map(preset => (
                      <Chip
                        key={preset.value}
                        label={preset.label}
                        size="small"
                        variant={syncCronSchedule === preset.value ? 'filled' : 'outlined'}
                        onClick={() => { setSyncCronSchedule(preset.value); setCronError(''); }}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: syncCronSchedule === preset.value ? darkGreenColor : undefined,
                          color: syncCronSchedule === preset.value ? 'white' : undefined,
                          borderColor: syncCronSchedule === preset.value ? darkGreenColor : undefined,
                          '&:hover': { bgcolor: syncCronSchedule === preset.value ? '#0f2e1f' : undefined }
                        }}
                        disabled={loadingConfig || savingConfig}
                      />
                    ))}
                  </Box>
                  <TextField
                    fullWidth
                    label="Cron Expression (UTC)"
                    value={syncCronSchedule}
                    onChange={e => { setSyncCronSchedule(e.target.value); setCronError(''); }}
                    error={!!cronError}
                    helperText={cronError || 'Standard 5-part cron (minute hour day month weekday). All times in UTC.'}
                    placeholder="30 0,2,4,6,8,10,12,14 * * *"
                    disabled={loadingConfig || savingConfig}
                    sx={{ fontFamily: 'monospace' }}
                    inputProps={{ style: { fontFamily: 'monospace' } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Button
                      variant="contained"
                      onClick={handleSaveConfig}
                      disabled={savingConfig || loadingConfig}
                      startIcon={savingConfig ? <LinearProgress size={20} /> : <SaveIcon />}
                      sx={{
                        backgroundColor: darkGreenColor,
                        '&:hover': { backgroundColor: '#0f2e1f' }
                      }}
                    >
                      {savingConfig ? 'Saving...' : 'Save Configuration'}
                    </Button>
                    
                    <Button
                      variant="outlined"
                      onClick={handleDownloadCsv}
                      disabled={downloading || loadingConfig || syncing}
                      startIcon={downloading ? <LinearProgress size={20} /> : <DownloadIcon />}
                    >
                      {downloading ? 'Downloading...' : 'Download CSV'}
                    </Button>

                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleForceSync}
                      disabled={syncing || loadingConfig}
                      startIcon={syncing ? <LinearProgress size={20} /> : <SyncIcon />}
                    >
                      {syncing ? 'Syncing...' : 'Force Sync SNYK Issues'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              {configSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {configSuccess}
                </Alert>
              )}

              {configError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {configError}
                </Alert>
              )}

              {syncResult && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    SNYK issues synced successfully!
                  </Alert>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Sync Summary
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Total Issues
                        </Typography>
                        <Typography variant="h6">
                          {syncResult.totalIssues?.toLocaleString() || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Rows Processed
                        </Typography>
                        <Typography variant="h6">
                          {syncResult.rowsProcessed?.toLocaleString() || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Team Assignments Preserved
                        </Typography>
                        <Typography variant="h6">
                          {syncResult.preservedAssignments?.toLocaleString() || 0}
                        </Typography>
                      </Grid>
                      {syncResult.severityCounts && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Severity Breakdown
                          </Typography>
                          <Box display="flex" gap={1} flexWrap="wrap">
                            {Object.entries(syncResult.severityCounts).map(([severity, count]) => (
                              <Chip
                                key={severity}
                                label={`${severity}: ${count}`}
                                size="small"
                                color={severity === 'Critical' ? 'error' : severity === 'High' ? 'warning' : 'default'}
                              />
                            ))}
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Closed Date (SLA Due Date) Calculation
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose which date to use as the starting point when calculating SLA due dates for open/unresolved issues.
                SLA windows: <strong>Critical 30 days</strong>, <strong>High 60 days</strong>, <strong>Medium 90 days</strong>, <strong>Low 180 days</strong>.
              </Typography>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <ToggleButtonGroup
                    value={closedDateMode}
                    exclusive
                    onChange={(_, val) => { if (val) setClosedDateMode(val); }}
                    size="small"
                  >
                    <ToggleButton value="firstIntroduced">
                      First Introduced Date
                    </ToggleButton>
                    <ToggleButton value="lastIntroduced">
                      Last Introduced Date
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    {closedDateMode === 'firstIntroduced'
                      ? 'SLA deadline is fixed from when the issue was first seen — does not reset on reintroduction.'
                      : 'SLA deadline resets each time the issue is reintroduced into the codebase.'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleRecalculate}
                    disabled={recalculating || loadingConfig}
                    startIcon={recalculating ? <LinearProgress size={20} /> : <RefreshIcon />}
                  >
                    {recalculating ? 'Recalculating...' : 'Recalculate Closed Dates'}
                  </Button>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    Save config first, then recalculate to apply the new mode to all existing issues.
                  </Typography>
                </Grid>
              </Grid>

              {recalcResult && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <strong>{recalcResult.message}</strong>
                  {' — '}
                  {recalcResult.totalIssues?.toLocaleString()} issues processed, {recalcResult.updatedCount?.toLocaleString()} updated.
                </Alert>
              )}

              <Divider sx={{ my: 3 }} />

              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'info.50' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  About SNYK Export:
                </Typography>
                <Typography variant="body2" paragraph>
                  The system uses SNYK Export API to generate CSV reports with the following filters:
                </Typography>
                <Box component="ul" sx={{ pl: 3, m: 0 }}>
                  <li><Typography variant="body2">Issue Status: Open</Typography></li>
                  <li><Typography variant="body2">Lifecycle: Production</Typography></li>
                  <li><Typography variant="body2">Product: Snyk Code, Snyk Open Source</Typography></li>
                  <li><Typography variant="body2">Project Origin: github-cloud-app</Typography></li>
                  <li><Typography variant="body2">Updated: From 2025-01-01</Typography></li>
                  <li><Typography variant="body2">Additional Filter: PROJECT_TARGET_SOURCE_TYPE = github-cloud-app</Typography></li>
                </Box>
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                  The CSV is automatically generated, downloaded, merged from multiple parts, and filtered before being made available for download.
                </Typography>
              </Paper>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default SnykAdmin;
